import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { round6 } from 'utilities/math';
import PairedTComponent from './Component';

import {
  getVisibleNumericalSelectedParametersNames,
  hasChosenNumericalVariablesEmptyCells,
  getFilteredParametersValuesNotEmpty,
  getNumericalSummary
} from 'store/dataset';

import { getHasNumericalVariablesEmptyCell } from 'store/scenes/inferentialStatistics/selectors';

import {
  setSampleSize,
  setSampleMeanDiff,
  setStandardDeviationDiff,
  setNumericalRowDataSet,
  setSampleMeanFirst,
  setSampleSizeFirst,
  setStandardDeviationFirst,
  setSampleMeanSecond,
  setSampleSizeSecond,
  setStandardDeviationSecond
} from 'store/form/inferentialStatistics/pairedT/actions';

import {
  getSampleSizeFirst,
  getSampleMeanFirst,
  getStandardDeviationFirst,
  getSampleSizeSecond,
  getSampleMeanSecond,
  isDatasetContainsRawData,
  getStandardDeviationSecond,
  getNumericalRowDataSetFirst,
  getNumericalRowDataSetSecond
} from 'store/form/inferentialStatistics/pairedT/selectors';

import { calculateSummaryVariable } from 'calculators/inferentialStatistics/pairedT';
import { getOptionListForDropDown } from 'calculators/inferentialStatistics/oneSampleT';

const roundAndFormatValue = value => round6(value).toFixed(6);

class PairedTContainer extends React.PureComponent {
  getNumericalOptionsDataSet = variableInAnotherDropDown => {
    const { numericalSelectedParametersNames, isFrequencyTable } = this.props;

    return getOptionListForDropDown({
      selectedParametersNames: numericalSelectedParametersNames,
      variableInAnotherDropDown,
      isFrequencyTable
    });
  };

  handleNumericalDropDownDataSetFirst = (_, newValue) => {
    this.props.handleNumericalDropDownDataSetFirst(newValue);
  };

  handleNumericalDropDownDataSetSecond = (_, newValue) => {
    this.props.handleNumericalDropDownDataSetSecond(newValue);
  };

  handleNumericalDropDownDataSet = (_, newValue) => {
    this.props.handleNumericalDropDownDataSet(newValue);
  };

  handleSampleSize = () => this.props.handleSampleSize();

  handleMeanDiffChange = () => this.props.handleMeanDiffChange();

  handleStandardDeviationDiff = () => this.props.handleStandardDeviationDiff();

  render () {
    return (
      <PairedTComponent
        {...this.props}
        getNumericalOptionsDataSet={this.getNumericalOptionsDataSet}
        onGenerateMeanDataSet={this.handleNumericalDropDownDataSet}
        onGenerateMeanDataSetFirst={this.handleNumericalDropDownDataSetFirst}
        onGenerateMeanDataSetSecond={this.handleNumericalDropDownDataSetSecond}
        onSampleSizeChange={this.handleSampleSize}
        onSampleMeanDiffChange={this.handleMeanDiffChange}
        onStandardDeviationDiffChange={this.handleStandardDeviationDiff}
      />
    );
  }
}

PairedTContainer.propTypes = {
  numericalSelectedParametersNames: PropTypes.arrayOf(PropTypes.string),
  handleNumericalDropDownDataSet: PropTypes.func.isRequired,
  handleNumericalDropDownDataSetFirst: PropTypes.func.isRequired,
  handleNumericalDropDownDataSetSecond: PropTypes.func.isRequired,
  handleSampleSize: PropTypes.func.isRequired,
  handleMeanDiffChange: PropTypes.func.isRequired,
  handleStandardDeviationDiff: PropTypes.func.isRequired,
  isFrequencyTable: PropTypes.bool.isRequired,
  firstVariable: PropTypes.string,
  secondVariable: PropTypes.string
};

const mapStateToProps = state => ({
  numericalSelectedParametersNames: getVisibleNumericalSelectedParametersNames(
    state
  ),
  isDatasetContainsRawData: isDatasetContainsRawData(state),
  sampleMeanFirst: getSampleMeanFirst(state),
  sampleSizeFirst: getSampleSizeFirst(state),
  standardDeviationFirst: getStandardDeviationFirst(state),
  sampleSizeSecond: getSampleSizeSecond(state),
  sampleMeanSecond: getSampleMeanSecond(state),
  standardDeviationSecond: getStandardDeviationSecond(state),
  firstVariable: getNumericalRowDataSetFirst(state),
  secondVariable: getNumericalRowDataSetSecond(state),
  hasNumericalVariablesEmptyCell: getHasNumericalVariablesEmptyCell(state)
});

const setDataToStore = (
  dispatch, {
    sampleMeanFirst,
    sampleMeanSecond,
    sampleSizeFirst,
    sampleSizeSecond,
    standardDeviationFirst,
    standardDeviationSecond
  }
) => {
  dispatch([
    setSampleSizeFirst(sampleSizeFirst),
    setSampleMeanFirst(roundAndFormatValue(sampleMeanFirst)),
    setStandardDeviationFirst(roundAndFormatValue(standardDeviationFirst))
  ]);

  dispatch([
    setSampleSizeSecond(sampleSizeSecond),
    setSampleMeanSecond(roundAndFormatValue(sampleMeanSecond)),
    setStandardDeviationSecond(
      roundAndFormatValue(standardDeviationSecond))
  ]);
};

const mapDispatchToProps = {
  handleNumericalDropDownDataSetFirst: value => (dispatch, getState) => {
    const state = getState();
    const numericalSummary = getNumericalSummary(state);
    const secondVariableName = getNumericalRowDataSetSecond(state);
    const secondSummaryVariable = numericalSummary[secondVariableName];
    const hasNumericalVariablesEmptyCell = getHasNumericalVariablesEmptyCell(state);
    if (!value) {
      dispatch([
        setSampleSizeFirst('N/A'),
        setSampleMeanFirst('N/A'),
        setStandardDeviationFirst('N/A')
      ]);

      if (secondVariableName && hasNumericalVariablesEmptyCell) {
        dispatch([
          setSampleSizeSecond(secondSummaryVariable.count),
          setSampleMeanSecond(roundAndFormatValue(secondSummaryVariable.mean)),
          setStandardDeviationSecond(
            roundAndFormatValue(secondSummaryVariable.sampleStandardDeviation))
        ]);
      }
      return;
    }
    const firstSummaryVariable = numericalSummary[value];

    if (!secondVariableName) {
      dispatch([
        setSampleSizeFirst(firstSummaryVariable.count),
        setSampleMeanFirst(roundAndFormatValue(firstSummaryVariable.mean)),
        setStandardDeviationFirst(
          roundAndFormatValue(firstSummaryVariable.sampleStandardDeviation))
      ]);
      return;
    }

    let countingSummary = {};
    if (hasNumericalVariablesEmptyCell) {
      if (hasChosenNumericalVariablesEmptyCells(state, value, secondVariableName)) {
        const variables = getFilteredParametersValuesNotEmpty(state, value, secondVariableName);
        const firstVariable = variables.firstParsedParameterValues;
        const secondVariable = variables.secondParsedParameterValues;
        countingSummary = calculateSummaryVariable(firstVariable, secondVariable);
        setDataToStore(dispatch, { ...countingSummary });
        return;
      }
    }

    countingSummary = {
      sampleMeanFirst: firstSummaryVariable.mean,
      sampleMeanSecond: secondSummaryVariable.mean,
      sampleSizeFirst: firstSummaryVariable.count,
      sampleSizeSecond: secondSummaryVariable.count,
      standardDeviationFirst: firstSummaryVariable.sampleStandardDeviation,
      standardDeviationSecond: secondSummaryVariable.sampleStandardDeviation
    };

    setDataToStore(dispatch, { ...countingSummary });
  },

  handleNumericalDropDownDataSetSecond: value => (dispatch, getState) => {
    const state = getState();
    const numericalSummary = getNumericalSummary(state);
    const firstVariableName = getNumericalRowDataSetFirst(state);
    const firstSummaryVariable = numericalSummary[firstVariableName];
    const hasNumericalVariablesEmptyCell = getHasNumericalVariablesEmptyCell(state);
    if (!value) {
      dispatch([
        setSampleSizeSecond('N/A'),
        setSampleMeanSecond('N/A'),
        setStandardDeviationSecond('N/A')
      ]);

      if (firstVariableName && hasNumericalVariablesEmptyCell) {
        dispatch([
          setSampleSizeFirst(firstSummaryVariable.count),
          setSampleMeanFirst(roundAndFormatValue(firstSummaryVariable.mean)),
          setStandardDeviationFirst(
            roundAndFormatValue(firstSummaryVariable.sampleStandardDeviation))
        ]);
      }
      return;
    }

    const secondSummaryVariable = numericalSummary[value];
    if (!firstVariableName) {
      dispatch([
        setSampleSizeSecond(secondSummaryVariable.count),
        setSampleMeanSecond(roundAndFormatValue(secondSummaryVariable.mean)),
        setStandardDeviationSecond(
          roundAndFormatValue(secondSummaryVariable.sampleStandardDeviation))
      ]);
      return;
    }

    let countingSummary = {};
    if (hasNumericalVariablesEmptyCell) {
      if (hasChosenNumericalVariablesEmptyCells(state, value, firstVariableName)) {
        const variables = getFilteredParametersValuesNotEmpty(state, firstVariableName, value);
        const firstVariable = variables.firstParsedParameterValues;
        const secondVariable = variables.secondParsedParameterValues;
        countingSummary = calculateSummaryVariable(firstVariable, secondVariable);
        setDataToStore(dispatch, { ...countingSummary });
        return;
      }
    }

    countingSummary = {
      sampleMeanFirst: firstSummaryVariable.mean,
      sampleMeanSecond: secondSummaryVariable.mean,
      sampleSizeFirst: firstSummaryVariable.count,
      sampleSizeSecond: secondSummaryVariable.count,
      standardDeviationFirst: firstSummaryVariable.sampleStandardDeviation,
      standardDeviationSecond: secondSummaryVariable.sampleStandardDeviation
    };

    setDataToStore(dispatch, { ...countingSummary });
  },

  handleNumericalDropDownDataSet: value => (dispatch, getState) => {
    const state = getState();
    const numericalSummary = getNumericalSummary(state);
    const valueDataSet = numericalSummary[value];
    if (!value) {
      dispatch([
        setSampleSize('10'),
        setSampleMeanDiff('0'),
        setStandardDeviationDiff('1')
      ]);
      return;
    }

    dispatch([
      setSampleSize(valueDataSet.count),
      setSampleMeanDiff(roundAndFormatValue(valueDataSet.mean)),
      setStandardDeviationDiff(
        roundAndFormatValue(valueDataSet.sampleStandardDeviation)
      )
    ]);
  },

  handleSampleSize: () => dispatch => dispatch(setNumericalRowDataSet('')),

  handleMeanDiffChange: () => dispatch => dispatch(setNumericalRowDataSet('')),

  handleStandardDeviationDiff: () => dispatch => dispatch(setNumericalRowDataSet(''))
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PairedTContainer);
